import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Plus, X, FileText } from 'lucide-react';

interface MeetingNotesProps {
  notes: string[];
  onNotesChange: (notes: string[]) => void;
  className?: string;
  readOnly?: boolean;
}

export const MeetingNotes = ({
  notes,
  onNotesChange,
  className,
  readOnly = false,
}: MeetingNotesProps) => {
  const { t } = useTranslation('oneOnOne');
  const [newNote, setNewNote] = useState('');

  const handleAddNote = () => {
    if (newNote.trim()) {
      onNotesChange([...notes, newNote.trim()]);
      setNewNote('');
    }
  };

  const handleRemoveNote = (index: number) => {
    onNotesChange(notes.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleAddNote();
    }
  };

  return (
    <Card
      className={`h-full flex flex-col border-none bg-white/90 shadow-lg backdrop-blur ${
        className ?? ''
      }`}
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl text-slate-900">
          <FileText className="h-5 w-5 text-primary" />
          {t('meetingNotes')}
        </CardTitle>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('meetingNotesDescription')}
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Existing Notes */}
        {notes.length > 0 && (
          <div className="space-y-2">
            {notes.map((note, index) => (
              <div
                key={index}
                className="group flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 transition-colors hover:bg-slate-100"
              >
                <div className="flex-1 min-w-0">
                  <p className="whitespace-pre-wrap text-sm text-slate-900">
                    {note}
                  </p>
                </div>
                {!readOnly && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveNote(index)}
                    className="shrink-0 h-8 w-8 p-0 opacity-0 transition-opacity group-hover:opacity-100"
                  >
                    <X className="h-4 w-4 text-slate-500" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add New Note */}
        {!readOnly && (
          <div className="space-y-2">
            <Textarea
              placeholder={t('addNotePlaceholder')}
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={3}
              className="resize-none"
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {t('pressEnterToAdd')}
              </p>
              <Button
                onClick={handleAddNote}
                disabled={!newNote.trim()}
                size="sm"
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                {t('addNote')}
              </Button>
            </div>
          </div>
        )}

        {/* Note Count */}
        {notes.length > 0 && (
          <div className="pt-2 border-t border-slate-200">
            <p className="text-xs text-muted-foreground text-center">
              {t('noteCount')(notes.length)}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
